/* eslint-disable prettier/prettier */
import React, { createContext, useMemo } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { AuthContextType, AuthProviderProps } from "./AuthTypes";
import { useAuthState } from "@/hooks/useAuthState";
import { useAuthActions } from "@/hooks/useAuthActions";
import { useAppSelector } from "@/store/hooks";
import {
    selectAppRoles,
    selectIsAdmin,
    selectIsReadOnlyUser
} from "@/store/selectors/authSelectors";
import { TokenService } from "@/service/TokenService";
import { AuthService } from "@/service/AuthService";
import { useAxiosInterceptor } from "@/context/auth/AuthAxiosInterceptor";

/**
 * Authentication context for sharing auth state across the application
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider component that wraps the application with authentication context
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const oidcAuth = useOidcAuth();

    // Get roles from userSlice via selectors
    const appRoles = useAppSelector(selectAppRoles);
    const isAdmin = useAppSelector(selectIsAdmin);
    const isReadOnlyUser = useAppSelector(selectIsReadOnlyUser);

    // Custom hooks for state management and actions
    useAuthState({
        oidcUser: oidcAuth.user ?? null,
        isLoading: oidcAuth.isLoading,
        error: oidcAuth.error ?? null,
        signinRedirect: oidcAuth.signinRedirect,
    });

    const { signIn, signOut, signInSilent, handleUnauthorized } = useAuthActions();

    // Memoized access token
    const accessToken = useMemo(() => {
        return (
            TokenService.extractAccessToken(oidcAuth.user ?? null) ||
            TokenService.getDevelopmentToken()
        );
    }, [oidcAuth.user]);

    // Set up Axios interceptor to attach access token to requests and handle 401 responses
    useAxiosInterceptor({
        accessToken,
        onUnauthorized: handleUnauthorized,
    });

    // Determine authentication state
    const isAuthenticated = useMemo(() => {
        return AuthService.isAuthenticated(oidcAuth.user ?? null);
    }, [oidcAuth.user]);

    // Memoized context value to prevent unnecessary re-renders
    const contextValue: AuthContextType = useMemo(
        () => ({
            signIn,
            signOut,
            signInSilent,
            isLoading: oidcAuth.isLoading,
            isAuthenticated,
            accessToken,
            user: oidcAuth.user ?? null,
            appRoles,
            isAdmin,
            isReadOnlyUser,
        }),
        [
            signIn,
            signOut,
            signInSilent,
            oidcAuth.isLoading,
            isAuthenticated,
            accessToken,
            oidcAuth.user,
            appRoles,
            isAdmin,
            isReadOnlyUser,
        ],
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;